import { Flame } from "lucide-react"

interface FigTreeProps {
  streakDays?: number
  bestStreak?: number
}

const CIRCUMFERENCE = 2 * Math.PI * 22 // ≈ 138.23

export function FigTree({ streakDays = 0, bestStreak = 0 }: FigTreeProps) {
  const progress = bestStreak > 0 ? Math.min(streakDays / bestStreak, 1) : 0
  const dashoffset = CIRCUMFERENCE * (1 - progress)
  const pct = bestStreak > 0 ? Math.round((streakDays / bestStreak) * 100) : 0

  return (
    <div className="mb-5" data-testid="fig-tree">
      <div
        className="relative flex items-center gap-3.5 overflow-clip rounded-2xl px-[18px] py-4"
        style={{ background: "var(--fig-streak-bg)" }}
      >
        {/* Decorative bokeh circles */}
        <div
          className="absolute -top-5 -right-2.5 size-[100px] rounded-full"
          style={{ background: "var(--fig-streak-bokeh)" }}
        />
        <div
          className="absolute top-5 right-[30px] size-[60px] rounded-[30px]"
          style={{ background: "var(--fig-streak-bokeh-sm)" }}
        />

        {/* Progress ring + flame icon */}
        <div className="relative size-[52px] shrink-0">
          <svg
            width="52"
            height="52"
            viewBox="0 0 52 52"
            className="absolute top-0 left-0"
          >
            <circle
              cx="26"
              cy="26"
              r="22"
              fill="none"
              stroke="var(--fig-streak-ring-track)"
              strokeWidth="4"
            />
            {progress > 0 && (
              <circle
                cx="26"
                cy="26"
                r="22"
                fill="none"
                stroke="var(--fig-streak-ring-fill)"
                strokeWidth="4"
                strokeLinecap="round"
                strokeDasharray={CIRCUMFERENCE}
                strokeDashoffset={dashoffset}
                transform="rotate(-90 26 26)"
              />
            )}
          </svg>
          <div className="absolute inset-0 flex items-center justify-center">
            <Flame size={18} style={{ color: "var(--fig-streak-ring-fill)" }} />
          </div>
        </div>

        {/* Text */}
        <div className="relative flex min-w-0 flex-1 flex-col gap-0.5">
          <span
            className="text-[22px] leading-[26px] font-bold tracking-[-0.02em]"
            style={{
              fontFamily: "'Fraunces', serif",
              color: "var(--fig-streak-text)",
            }}
          >
            {streakDays === 0
              ? "No streak yet"
              : `${streakDays} day${streakDays !== 1 ? "s" : ""}`}
          </span>
          <span
            className="text-[12px] leading-4 font-medium"
            style={{
              fontFamily: "'Figtree', sans-serif",
              color: "var(--fig-streak-text-muted)",
            }}
          >
            {streakDays === 0
              ? "Start logging to build your streak"
              : bestStreak > 0
                ? `${pct}% to your best of ${bestStreak}`
                : "Keep it going!"}
          </span>
        </div>
      </div>
    </div>
  )
}
